import { dedupeConcurrentRequest } from "@/app/_utils/dedupeConcurrentRequest";
import { getCsrfToken } from "@/app/_utils/csrf";

export type FestivalShowRow = {
  id: string;
  title: string;
  slug: string;
  start: string;
  img_url?: string | null;
  position: number;
};

export type Festival = {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  img_url?: string | null;
  published: boolean;
  created_at: string;
  updated_at: string;
  shows: FestivalShowRow[];
  tickets_sold: number;
  tickets_available: number;
  sell_through_pct: number;
};

export type UpcomingShowOption = {
  id: string;
  title: string;
  start: string;
  img_url?: string | null;
};

export type FestivalKpis = {
  revenue: number;
  num_shows: number;
  avg_capacity: number;
  tickets_available: number;
  tickets_sold: number;
  avg_purchase_size: number;
  avg_age: number | null;
};

export type FestivalSalesPoint = {
  date: string | null;
  cumulative_revenue: number;
};

export type FestivalSales = {
  sell_through_pct: number;
  tickets_sold: number;
  tickets_available: number;
  gross_revenue: number;
  avg_ticket_price: number;
  refund_count: number;
  refund_amount: number;
  series: FestivalSalesPoint[];
};

export type FestivalDetailShowRow = {
  id: string;
  title: string;
  variant_name?: string | null;
  start: string;
  img_url?: string | null;
  capacity: number;
  sold: number;
  revenue: number;
  ticket_price: number;
  status: string;
};

export type FestivalRecentOrder = {
  id: string;
  guest_name: string | null;
  quantity: number;
  total_price: number;
  show_title: string | null;
  created_at: string;
};

export type FestivalDetail = {
  festival: Festival;
  kpis: FestivalKpis;
  sales: FestivalSales;
  shows: FestivalDetailShowRow[];
  recent_orders: FestivalRecentOrder[];
};

class FestivalService {
  static instance: FestivalService;
  csrfToken = getCsrfToken();
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;
  private readonly inflight = new Map<string, Promise<unknown>>();

  getFestivalsAsync = async (): Promise<Festival[]> => {
    return dedupeConcurrentRequest(this.inflight, "list", async () => {
      const res = await fetch(`${this.baseUrl}festival/`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      return await res.json();
    }) as Promise<Festival[]>;
  };

  getFestivalAsync = async (id: string): Promise<Festival> => {
    const res = await fetch(`${this.baseUrl}festival/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getFestivalDetailAsync = async (id: string): Promise<FestivalDetail> => {
    const res = await fetch(`${this.baseUrl}festival/${id}/detail/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getFestivalGuestsAsync = async (id: string): Promise<any[]> => {
    const res = await fetch(`${this.baseUrl}festival/${id}/guests/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getUpcomingShowsAsync = async (): Promise<UpcomingShowOption[]> => {
    const res = await fetch(`${this.baseUrl}festival/upcoming-shows/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  createFestivalAsync = async (festival: {
    title: string;
    description?: string | null;
    show_ids?: string[];
    image?: string | null;
  }): Promise<Festival> => {
    const res = await fetch(`${this.baseUrl}festival/`, {
      method: "POST",
      body: JSON.stringify(festival),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    return await res.json();
  };

  updateFestivalAsync = async (
    id: string,
    festival: {
      title?: string;
      description?: string | null;
      show_ids?: string[];
      image?: string | null;
    }
  ): Promise<Festival> => {
    const res = await fetch(`${this.baseUrl}festival/${id}/`, {
      method: "PUT",
      body: JSON.stringify(festival),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    return await res.json();
  };

  deleteFestivalAsync = async (id: string) => {
    return await fetch(`${this.baseUrl}festival/${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": this.csrfToken },
      credentials: "include",
    });
  };

  publishFestivalAsync = async (id: string, published: boolean): Promise<Festival> => {
    const res = await fetch(`${this.baseUrl}festival/${id}/publish/`, {
      method: "POST",
      body: JSON.stringify({ published }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
      credentials: "include",
    });
    return await res.json();
  };

  getPreviewTokenAsync = async (id: string): Promise<string> => {
    const res = await fetch(`${this.baseUrl}festival/${id}/preview-token/`, {
      method: "POST",
      headers: { "X-CSRFToken": this.csrfToken },
      credentials: "include",
    });
    if (!res.ok) throw new Error("Failed to create preview link");
    return (await res.json()).token;
  };
}

export default new FestivalService();
